/**
 * Helpers for rendering EXPORT_READY_FOR_DOWNLOAD notification descriptions.
 * Extracted so the description-processing logic can be unit-tested without
 * mounting the host Vue components (HERA-8159).
 *
 * The original presigned download URL baked into a notification's description
 * is signed with the producing Lambda's temporary STS credentials, so it
 * expires (~12h) long before the notification's 7-day TTL. New-format
 * notifications carry an `s3Key` in their payload; the frontend regenerates a
 * fresh URL on click via rdsApi /downloadS3File. For those, the stale inline
 * <a href> must be neutralized so a click routes through regeneration instead
 * of opening the dead link (which returns an S3 ExpiredToken error).
 */

// Matches an opening anchor tag with any attributes, e.g. <a href="..." download="...">
const ANCHOR_OPEN_TAG = /<a\b[^>]*>/gi;

/**
 * Safely parse a notification's JSON payload. Returns {} on any error.
 * @param {{payload?: string}} notification
 * @returns {object}
 */
export function parseNotificationPayload(notification) {
  try {
    return JSON.parse(notification.payload) || {};
  } catch (e) {
    return {};
  }
}

/**
 * Process an export notification's description HTML for safe rendering.
 *
 * - New-format (payload.s3Key present): strip attributes from inline anchors so
 *   a click bubbles to the row handler, which regenerates a fresh presigned URL.
 * - Legacy (no s3Key) with legacyStopPropagation=true: keep the inline link but
 *   add onclick="event.stopPropagation()" so it does not trigger the
 *   (non-clickable) row handler.
 * - Otherwise: return the description unchanged.
 *
 * @param {{payload?: string, description?: string}} notification
 * @param {{legacyStopPropagation?: boolean}} [options]
 * @returns {string}
 */
export function processExportNotificationDescription(notification, options = {}) {
  const html = notification.description || '';
  const payload = parseNotificationPayload(notification);
  if (payload && payload.s3Key) {
    return html.replace(ANCHOR_OPEN_TAG, '<a>');
  }
  if (options.legacyStopPropagation) {
    return html.replace(/<a /g, '<a onclick="event.stopPropagation()" ');
  }
  return html;
}

// The payload.source value athena stamps on channel-provisioning-failure notifications
// (see athena provisioningFailureNotification.ts). Must stay in sync with the backend.
const PROVISIONING_FAILURE_SOURCE = 'MESSENGER_CHANNEL_PROVISIONING_FAILURE';

/**
 * HERA-8984 — map an MSG-53 "channel provisioning failed" notification payload to an in-app route so
 * the DSP lands on the recovery screen. Associate → Associate List with `openAddAssociate` set (the
 * list opens the Add Associate drawer on that query). Hera user → New User screen with the tenant
 * context, falling back to the users list when ids are missing. Returns null for any other
 * notification so callers fall through to their existing handling.
 *
 * @param {object} payload parsed notification payload
 * @returns {object|null} a vue-router location, or null when this is not an MSG-53 alert
 */
export function provisioningFailureRoute(payload) {
  if (!payload || payload.source !== PROVISIONING_FAILURE_SOURCE) return null;
  if (payload.entityKind === 'user') {
    if (payload.tenantId && payload.tenantGroup) {
      return {
        name: 'SettingsUserDetail',
        query: { tenantId: payload.tenantId, group: payload.tenantGroup }
      };
    }
    return { path: '/settings/users' };
  }
  // associate (default) — land on the Associate List. status=0 / labels=-1 / preferredDaysOff=-1 are
  // the list's "no filter (show all active)" sentinels; passing them resets any narrow filters the
  // list had stored (otherwise it shows the wrong subset) and matches the backend redirectLink.
  // openAddAssociate tells the list to open the Add Associate drawer.
  return {
    name: 'associate-list',
    query: { status: '0', labels: '-1', preferredDaysOff: '-1', openAddAssociate: '1' }
  };
}

// The Athena path for Scheduling & Rostering > Planning, relative to the `/ath` prefix that
// goToAthena (src/main.js) applies. Used as the fallback when a SCHEDULING_SHIFTS_IMPORT_COMPLETE
// payload carries no link of its own.
export const PLANNING_ATHENA_PATH = '/scheduler/planning';

/**
 * HA-2978 Deliverable B — decide where a SCHEDULING_SHIFTS_IMPORT_COMPLETE notification click
 * should go. Extracted as a pure function so the success/failure decision can be unit-tested
 * without mounting the host components, following the HERA-8159 precedent above.
 *
 * The discriminator is the terminal job status the hera-ai promoter stamps on the in-app payload
 * (`COMPLETED` | `FAILED`), NOT the presence of a link. The promoter carries the Athena Planning
 * path on SUCCESS and omits the link on failure, so branching on link presence inverts both legs:
 * a successful import would be pushed at hera's router (which does not own `/ath/*`) and a failed
 * one would be sent to Planning as though it had worked, which HA-2978 AC 4 forbids.
 *
 * @param {object} [inApp] the notification's parsed `payload.inApp`
 * @returns {{kind: 'athena'|'internal'|'none', path?: string}}
 *   `athena`   — cross-app, caller must use goToAthena + window.location.href
 *   `internal` — a hera route, caller may use $router.push
 *   `none`     — a failed import with nowhere useful to send the user; do not navigate
 */
export function shiftImportCompleteDestination(inApp) {
  if (inApp && inApp.status === 'FAILED') {
    // Never present a failed import as a success. Only navigate when the payload offers an
    // internal hera link to review or retry with.
    return inApp.link ? { kind: 'internal', path: inApp.link } : { kind: 'none' };
  }
  // Success (or a payload predating the status field): the import landed in the Athena scheduler.
  return { kind: 'athena', path: (inApp && inApp.link) || PLANNING_ATHENA_PATH };
}

/**
 * HERA-9207 — resolve the Athena path for a PAYROLL_PROVIDER_MATCHES_FOUND notification click.
 *
 * The producer stamps `payload.inApp.link` as an Athena-absolute path ('/ath/payroll-provider/sync').
 * goToAthena (src/main.js) already prepends `/ath`, so a leading '/ath' segment is stripped here to
 * avoid '/ath/ath/...'. Only safe same-origin relative paths are accepted (start with a single '/',
 * never '//' or a backslash variant), so a tampered payload cannot send the user off-site.
 *
 * @param {object} [inApp] the notification's parsed `payload.inApp`
 * @returns {string|null} path relative to `/ath`, or null when there is nothing safe to follow
 */
export function payrollProviderMatchesPath(inApp) {
  const link = inApp && inApp.link;
  if (typeof link !== 'string' || !link.startsWith('/') || link.startsWith('//') || link.includes('\\')) {
    return null;
  }
  const stripped = link === '/ath' || link.startsWith('/ath/') || link.startsWith('/ath?') || link.startsWith('/ath#')
    ? link.slice('/ath'.length)
    : link;
  if (stripped === '' || stripped.startsWith('//')) {
    return null;
  }
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}
